import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { selector } from 'apps/services';
import { withTranslation } from 'react-i18next';
import { isEmail, isEmpty } from 'validator';
import { Footer } from 'components/core';
import { filter } from 'lodash';
import { attemptSetLanguage } from 'apps/modules/app/AppActions';
import { ENGLISH_LANG, MAIN_ROUTE, LANGUAGES, REQUIRED, CONST_BANKGIRO, CONST_IBAN, TOAST_AUTO_CLOSE, TOAST_POSITION, COUNTRIES_EXTN, PREFERRED_COUNTRIES } from 'configs/constants';
import { attemptStartRental, attemptResetNotificationState, attemptGetQuickshop } from 'apps/modules/quickshopweb/QuickshopWebActions';
import { useParams } from 'react-router-dom';
import { FormGroup, Label, Input } from 'reactstrap';
import { getText } from 'helpers/app/getText';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { produce, setAutoFreeze } from 'immer';
import { useNavigate } from 'react-router-dom';

function Rental({ app, quickshopweb, attemptGetQuickshop, attemptStartRental, attemptSetLanguage }) {
  setAutoFreeze(false);
  const history = useNavigate();
  const { quickshopid } = useParams();

  const [isExistingCustomer, setIsExistingCustomer] = useState(true);
  const [quickShopRentalCustomerId, setQuickshopRentalCustomerId] = useState('');
  const [fields, setFields] = useState({
    merchantID: '',
    componentID: '',
    customerID: '',
    customerName: '',
    vatNumber: '',
    contactName: '',
    mobilePhone: '',
    email: '',
    bankDetails: {
      account: '',
      accountType: CONST_BANKGIRO,
    },
  });

  const [errors, setErrors] = useState({
    customerID: '',
    customerName: '',
    vatNumber: '',
    contactName: '',
    mobilePhone: '',
    email: '',
  });

  useEffect(() => {
    attemptGetQuickshop(quickshopid);
  }, []);

  useEffect(() => {
    if (quickshopweb.get('quickshopWeb')?._id) {
      const quickshopObj = quickshopweb.get('quickshopWeb');
      setFields(
        produce((draft) => {
          draft.merchantID = quickshopObj.merchantID;
          draft.componentID = quickshopObj.evCharging.componentID;
        }),
      );
    }
  }, [JSON.stringify(quickshopweb.get('quickshopWeb'))]);

  useEffect(() => {
    if (quickshopweb.get('isQuickshopRentalStarted') === true) {
      attemptResetNotificationState('isQuickshopRentalStarted');
      setQuickshopRentalCustomerId(quickshopweb.get('quickShopRentalCustomerId'));
    }
  }, [quickshopweb.get('isQuickshopRentalStarted')]);

  useEffect(() => {
    if (quickshopweb.get('error')?.length > 0) {
      const error = quickshopweb.get('error');
      if (error?.code === 404) {
        toast.dismiss();
        toast.error(getText('notifications:CUSTOMER_ALREADY_EXISTS_FOR_RENTAL_MSG'), { autoClose: 3000 });
      }
    }
  }, [quickshopweb.get('error')]);

  const onLoadQuickshop = () => {
    history(MAIN_ROUTE(quickshopweb.get('quickshopWeb')?._id));
  };

  const onChange = (ev) => {
    const { name, value } = ev.target;

    setFields(
      produce((draft) => {
        draft[name] = value;
      }),
    );
  };

  const onSetBankAccountType = (val) => {
    setFields(
      produce((draft) => {
        draft.bankDetails.accountType = val;
      }),
    );
  };

  const validateNewCustomerDetails = (name, value) => {
    switch (name) {
      case 'customerName':
        if (isEmpty(value)) {
          return REQUIRED('Customer name', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
      case 'contactName':
        if (isEmpty(value)) {
          return REQUIRED('Contact name', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
      case 'email':
        if (isEmpty(value)) {
          return REQUIRED('Email', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else if (!isEmail(value)) {
          return getText('notifications:INVALID_EMAIL_MSG');
        } else {
          return '';
        }
      case 'mobilePhone':
        if (isEmpty(value)) {
          return REQUIRED('Phone', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
      case 'vatNumber':
        if (isEmpty(value)) {
          return REQUIRED('VAT number', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
    }
  };

  const validateExistingCustomerDetails = (name, value) => {
    switch (name) {
      case 'customerID':
        if (isEmpty(value)) {
          return REQUIRED('Customer ID', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
    }
  };

  const validateBankDetails = (name, value) => {
    switch (name) {
      case 'account':
        if (isEmpty(value)) {
          return REQUIRED('Bank account', getText('notifications:FIELD_VALIDATION_REQUIRED_MSG'));
        } else {
          return '';
        }
    }
  };

  const onRentNow = () => {
    let errors = {};

    if (isExistingCustomer) {
      Object.keys(fields).map((name) => {
        const error = validateExistingCustomerDetails(name, fields[name]);
        if (error?.length > 0) {
          errors[name] = error;
        }
      });
    } else {
      Object.keys(fields).map((name) => {
        const error = validateNewCustomerDetails(name, fields[name]);
        if (error?.length > 0) {
          errors[name] = error;
        }
      });
      Object.keys(fields.bankDetails).map((name) => {
        const error = validateBankDetails(name, fields.bankDetails[name]);
        if (error?.length > 0) {
          errors[name] = error;
        }
      });
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      toast.dismiss();
      toast.error(getText('notifications:VALIDATION_ERRORS_MSG'), { autoClose: 3000 });
      return;
    }
    attemptStartRental({
      merchantID: fields.merchantID,
      componentID: fields.componentID,
      customerID: fields.customerID,
      ...(fields.customerName && { customerName: fields.customerName }),
      ...(fields.vatNumber && { vatNumber: fields.vatNumber }),
      ...(fields.contactName && { contactName: fields.contactName }),
      ...(fields.mobilePhone && { mobilePhone: fields.mobilePhone }),
      ...(fields.email && { email: fields.email }),
      ...(fields.bankDetails && { bankDetails: fields.bankDetails }),
    });
  };

  const onCustomerMobilePhoneChange = (val) => {
    setFields(
      produce((draft) => {
        draft.mobilePhone = val;
      }),
    );
  };

  const onLanguageChange = (opt) => {
    const lang = filter(LANGUAGES, { code: opt.code })?.[0]?.code || LANGUAGES[0].code;
    attemptSetLanguage(lang);
  };

  const onBankDetailsChange = (ev) => {
    const { value } = ev.target;

    setFields(
      produce((draft) => {
        draft.bankDetails.account = value;
      }),
    );
  };

  const quickshopWeb = quickshopweb.get('quickshopWeb');
  const language = app.get('language') || ENGLISH_LANG;

  return [
    <ToastContainer key="1" autoClose={TOAST_AUTO_CLOSE} position={TOAST_POSITION} />,
    <div className="my-3 p-3 text-center" key="5">
      <div className={`${quickShopRentalCustomerId?.length > 0 ? `rental-div-confirm` : `rental-div`} text-center d-inline-block`}>
        {quickShopRentalCustomerId?.length > 0 ? (
          <div>
            {!isExistingCustomer && <p>{getText('rental:THANK_YOU_FOR_RENTAL_TEXT')}</p>}
            {isExistingCustomer && <p>{getText('rental:THANK_YOU_FOR_RENTING_QUICKSHOP')}</p>}
            <div className="rental-customer-div">
              <p>{getText('rental:YOUR_CUSTOMER_ID_MSG')}:</p>
              <p>{quickShopRentalCustomerId}</p>
            </div>
            <p>{getText('rental:PLEASE_USE_CUSTOMER_ID_TEXT')}</p>
            <FormGroup className="text-center">
              <button type="button" onClick={() => onLoadQuickshop()} className="btn btn-success mt-20">
                {getText('rental:LOAD_THE_QUICKSHOP')}
              </button>
            </FormGroup>
          </div>
        ) : (
          <div>
            <div className="header-div text-center d-inline-block">
              <img src={quickshopWeb.imageURL || ''} className="header-img mb-20" />
            </div>
            <p>{getText('rental:START_RENTING_THIS_CONNECTOR')}</p>
            <FormGroup className="text-left mb-1" onClick={() => setIsExistingCustomer(true)}>
              <Label check className="ml-20 pt-12">
                <Input type="radio" name="customerType" checked={isExistingCustomer} onChange={() => setIsExistingCustomer(true)} />
                {getText('rental:EXISTING_CUSTOMER')}
              </Label>
            </FormGroup>
            {isExistingCustomer && (
              <FormGroup className="text-left">
                <label>{getText('rental:CUSTOMER_ID')}:</label>
                <Input className={`${errors.customerID && `input-normal-error-msg`} smaller-font`} type="text" name="customerID" value={fields.customerID || ''} onChange={(val) => onChange(val)} />
                <p className={`error-msg ${!errors.customerID && 'hidden'}`}>{errors.customerID}</p>
              </FormGroup>
            )}
            <FormGroup className="text-left mb-1" onClick={() => setIsExistingCustomer(false)}>
              <Label check className="ml-20 pt-12">
                <Input type="radio" name="customerType" checked={!isExistingCustomer} onChange={() => setIsExistingCustomer(false)} />
                {getText('rental:NEW_CUSTOMER')}
              </Label>
            </FormGroup>
            {!isExistingCustomer && (
              <div>
                <FormGroup className="text-left">
                  <label>{getText('rental:COMPANY_NAME')}:</label>
                  <Input className={`${errors.customerName && `input-normal-error-msg`}`} maxLength={100} type="text" name="customerName" value={fields.customerName || ''} onChange={(val) => onChange(val)} />
                  <p className={`error-msg ${!errors.customerName && 'hidden'}`}>{errors.customerName}</p>
                </FormGroup>
                <FormGroup className="text-left">
                  <label>{getText('rental:EU_VAT_NUMBER')}:</label>
                  <Input className={`${errors.vatNumber && `input-normal-error-msg`}`} maxLength={30} type="text" name="vatNumber" value={fields.vatNumber || ''} onChange={(val) => onChange(val)} />
                  <p className={`error-msg ${!errors.vatNumber && 'hidden'}`}>{errors.vatNumber}</p>
                </FormGroup>
                <FormGroup className="text-left">
                  <label>{getText('rental:CONTACT_PERSON_NAME')}:</label>
                  <Input className={`${errors.contactName && `input-normal-error-msg`}`} maxLength={100} type="text" name="contactName" value={fields.contactName || ''} onChange={(val) => onChange(val)} />
                  <p className={`error-msg ${!errors.contactName && 'hidden'}`}>{errors.contactName}</p>
                </FormGroup>
                <FormGroup className="text-left">
                  <label className="mb-0 mt-1">{getText('rental:MOBILE_PHONE')}</label>
                  <ReactPhoneInput
                    onBlur={(ev, country) => {
                      const phone = ev.target.value;
                      const value = phone.replace(`+${country.dialCode}`, '');
                      if (value.substring(0, 1) === '0') {
                        onCustomerMobilePhoneChange(`+${country.dialCode}${value.substring(1, value.length)}`);
                      }
                    }}
                    onlyCountries={COUNTRIES_EXTN.map((country) => country.code)}
                    value={fields.mobilePhone}
                    countryCodeEditable={false}
                    autoFormat={false}
                    className={`wd-300 ${errors.mobilePhone && `input-normal-error-msg`}`}
                    inputExtraProps={{ name: 'phone' }}
                    preferredCountries={PREFERRED_COUNTRIES}
                    onChange={(val) => onCustomerMobilePhoneChange(val)}
                  />
                  <p className={`error-msg ${!errors.mobilePhone && 'hidden'}`}>{errors.mobilePhone}</p>
                </FormGroup>
                <FormGroup className="text-left">
                  <label>{getText('rental:EMAIL')}:</label>
                  <Input className={`${errors.email && `input-normal-error-msg`}`} maxLength={200} type="text" name="email" value={fields.email || ''} onChange={(val) => onChange(val)} />
                  <p className={`error-msg ${!errors.email && 'hidden'}`}>{errors.email}</p>
                </FormGroup>
                <FormGroup className="text-left mb-1">
                  <Label check className="pt-12">
                    {getText('rental:BANK_ACCOUNT_FOR_PAYOUT')}
                  </Label>
                  <FormGroup className="text-left mb-1" onClick={() => onSetBankAccountType(CONST_BANKGIRO)}>
                    <Label check className="ml-20 pt-12">
                      <Input type="radio" name="bankAccountType" checked={fields.bankDetails.accountType === CONST_BANKGIRO} onChange={() => onSetBankAccountType(CONST_BANKGIRO)} />
                      {getText('rental:BANKGIRO')}
                    </Label>
                  </FormGroup>
                  {fields.bankDetails.accountType === CONST_BANKGIRO && (
                    <FormGroup className="text-left">
                      <Input className={`${errors.account && `input-normal-error-msg`}`} maxLength={40} placeholder={getText('rental:BANKGIRO_ACCOUNT')} type="text" name="account" value={fields.bankDetails.account || ''} onChange={(val) => onBankDetailsChange(val)} />
                      <p className={`error-msg ${!errors.account && 'hidden'}`}>{errors.account}</p>
                    </FormGroup>
                  )}
                  <FormGroup className="text-left mb-1" onClick={() => onSetBankAccountType(CONST_IBAN)}>
                    <Label check className="ml-20 pt-12">
                      <Input type="radio" name="bankAccountType" checked={fields.bankDetails.accountType === CONST_IBAN} onChange={() => onSetBankAccountType(CONST_IBAN)} />
                      {getText('rental:IBAN')}
                    </Label>
                  </FormGroup>
                  {fields.bankDetails.accountType === CONST_IBAN && (
                    <FormGroup className="text-left">
                      <Input className={`${errors.account && `input-normal-error-msg`}`} maxLength={40} placeholder={getText('rental:IBAN_ACCOUNT')} type="text" name="account" value={fields.bankDetails.account || ''} onChange={(val) => onBankDetailsChange(val)} />
                      <p className={`error-msg ${!errors.account && 'hidden'}`}>{errors.account}</p>
                    </FormGroup>
                  )}
                </FormGroup>
              </div>
            )}
            <FormGroup className="text-left">
              <button type="button" onClick={() => onRentNow()} className="btn btn-success mt-20">
                {getText('main:RENT_NOW')}
              </button>
            </FormGroup>
          </div>
        )}
      </div>
    </div>,
    <br />,
    <Footer key="6" moreInfoURL={quickshopWeb?.moreInfoURL} privacyPolicyURL={quickshopWeb?.privacyPolicyURL} customerAgreementURL={quickshopWeb?.customerAgreementURL} pageLanguages={LANGUAGES} onLanguageChange={(opt) => onLanguageChange(opt)} defaultLanguage={language?.length > 0 && filter(LANGUAGES, { code: language }).length > 0 ? filter(LANGUAGES, { code: language })[0]?.value : LANGUAGES[0].value} />,
  ];
}

const mapStateToProps = (state) => selector(state, false, ['authorisation', 'app', 'quickshopweb']);

const mapDispatchToProps = (dispatch) => {
  return {
    attemptStartRental: (data) => dispatch(attemptStartRental(data)),
    attemptGetQuickshop: (data) => dispatch(attemptGetQuickshop(data)),
    attemptSetLanguage: (data) => dispatch(attemptSetLanguage(data)),
    attemptResetNotificationState: (data) => dispatch(attemptResetNotificationState(data)),
  };
};

export default withTranslation('rental')(connect(mapStateToProps, mapDispatchToProps)(Rental));
